/**
 * Bug reports (the /bugs page, netlify/functions/api/bugs.ts): anyone signed in reports what
 * broke, after a human check; a report waits for an admin, who publishes it, rejects it, or
 * marks it fixed; published reports are read by everyone, signed in or not. No email is asked
 * for or shown: a report carries the reporter's first name only. Kept in Firestore `bug_reports`
 * (written by the server function alone).
 */

export type BugArea = 'canvas' | 'ideas' | 'screenplay' | 'library' | 'settings' | 'sharing' | 'desktop' | 'other';
export const BUG_AREAS: { id: BugArea; label: string }[] = [
    { id: 'canvas', label: 'The canvas and its nodes' },
    { id: 'ideas', label: 'The Ideas board' },
    { id: 'screenplay', label: 'The screenplay' },
    { id: 'library', label: 'The library and projects' },
    { id: 'settings', label: 'Settings and keys' },
    { id: 'sharing', label: 'Sharing and people' },
    { id: 'desktop', label: 'The desktop app' },
    { id: 'other', label: 'Something else' },
];
export const areaLabel = (area: BugArea): string => BUG_AREAS.find(a => a.id === area)?.label ?? 'Something else';

export type BugStatus = 'pending' | 'published' | 'rejected';

export interface BugReport {
    id: string;
    title: string;
    details: string;
    area: BugArea;
    status: BugStatus;
    createdAt: number;
    reporterId: string;
    /** The reporter's first name, as they gave it; never an email */
    reporterName: string;
    reviewedAt?: number;
    /** Published and since fixed */
    fixed?: boolean;
    /** The admin's word on it, shown with the report ("Fixed in the 14 October build") */
    note?: string;
}

/** What everyone may read of a report: who reported it is a first name only */
export type PublicBugReport = Omit<BugReport, 'reporterId'>;

export const MAX_TITLE = 120;
export const MAX_DETAILS = 2000;
export const MAX_NOTE = 300;
/** How many reports one person may send in a day */
export const REPORTS_A_DAY = 5;
export const DAY_MS = 24 * 60 * 60 * 1000;

const text = (value: unknown, max: number): string => (typeof value === 'string' ? value.replace(/\r\n?/g, '\n').replace(/[ \t]+\n/g, '\n').trim().slice(0, max) : '');
const isArea = (value: unknown): value is BugArea => typeof value === 'string' && BUG_AREAS.some(a => a.id === value);

/** The first name of what someone signs as; "Someone" when there is none */
export const firstName = (name: unknown): string => {
    const first = typeof name === 'string' ? name.trim().split(/\s+/)[0] : '';
    return first && !first.includes('@') ? first.slice(0, 40) : 'Someone';
};

/** A report as typed, cleaned, or why it cannot be sent */
export const cleanReport = (input: unknown): { ok: true; title: string; details: string; area: BugArea } | { ok: false; error: string } => {
    const data = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
    const title = text(data.title, MAX_TITLE);
    const details = text(data.details, MAX_DETAILS);
    const area = isArea(data.area) ? data.area : 'other';
    if (title.length < 4) return { ok: false, error: 'Give the report a title of a few words.' };
    if (details.length < 10) return { ok: false, error: 'Say what happened, in a sentence or two at least.' };
    return { ok: true, title, details, area };
};

/** A report as read back from the database, or null when it is not one */
export const toReport = (id: string, data: Record<string, unknown>): BugReport | null => {
    if (typeof data.title !== 'string' || typeof data.details !== 'string' || typeof data.reporterId !== 'string') return null;
    const status: BugStatus = data.status === 'published' || data.status === 'rejected' ? data.status : 'pending';
    return {
        id,
        title: data.title,
        details: data.details,
        area: isArea(data.area) ? data.area : 'other',
        status,
        createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
        reporterId: data.reporterId,
        reporterName: firstName(data.reporterName),
        ...(typeof data.reviewedAt === 'number' ? { reviewedAt: data.reviewedAt } : {}),
        ...(data.fixed === true ? { fixed: true } : {}),
        ...(typeof data.note === 'string' && data.note.trim() ? { note: data.note.trim().slice(0, MAX_NOTE) } : {}),
    };
};

/** A report as everyone may see it */
export const publicReport = ({ reporterId: _who, ...rest }: BugReport): PublicBugReport => rest;

export const REVIEW_ACTIONS = ['publish', 'reject', 'fix', 'reopen', 'unpublish'] as const;
export type ReviewAction = typeof REVIEW_ACTIONS[number];
export const isReviewAction = (value: unknown): value is ReviewAction => typeof value === 'string' && (REVIEW_ACTIONS as readonly string[]).includes(value);

/** What an admin's decision changes on a report; a note replaces the one there (an empty one takes it off) */
export const applyReview = (action: ReviewAction, note: unknown, now: number): Partial<BugReport> & { note?: string } => {
    const cleanNote = text(note, MAX_NOTE);
    const base: Partial<BugReport> = { reviewedAt: now, note: cleanNote };
    switch (action) {
        case 'publish': return { ...base, status: 'published', fixed: false };
        case 'reject': return { ...base, status: 'rejected', fixed: false };
        case 'fix': return { ...base, status: 'published', fixed: true };
        case 'reopen': return { ...base, status: 'published', fixed: false };
        case 'unpublish': return { ...base, status: 'pending', fixed: false };
    }
};

/** Newest first */
export const newestFirst = <T extends { createdAt: number; id: string }>(reports: T[]): T[] =>
    [...reports].sort((a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id));

/** How many of a person's reports fall within the last day */
export const sentToday = (reports: ReadonlyArray<Pick<BugReport, 'createdAt'>>, now: number): number =>
    reports.filter(r => now - r.createdAt < DAY_MS).length;
